import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  GetAdvancedParams,
  ColumnValuesPayload,
  ColumnValuesResponse,
  GetParams,
  GetResponse,
  PostResponse,
  Project,
  ProjectCreate,
} from '@app/shared';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  CompanyService,
  convertGetParamsToHttpParams,
  setGetParamsById,
} from '@app/core';
import {
  ProjectContainer,
  ProjectContainerCreate,
} from '../../shared/models/project-container';

@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  /** URLs principales relacionadas con Project **/
  projectUrl = environment.coreApiBaseUrl + '/api/project';
  projectContainerUrl = environment.coreApiBaseUrl + '/api/projectContainer';

  constructor(
    private http: HttpClient,
    private companyService: CompanyService
  ) {}

  /** Metodos de obtener/crear/actualizar/eliminar Project **/
  getProject(getParams: GetParams): Observable<GetResponse> {
    const params = convertGetParamsToHttpParams(getParams);
    const headers = this.companyService.getCompanyHeaders();

    return this.http.get<GetResponse>(this.projectUrl, {
      params,
      headers,
    });
  }

  getProjectByCompanies(getParams: GetParams): Observable<GetResponse> {
    const params = convertGetParamsToHttpParams({
      ...getParams,
      companyId: this.companyService.selectedCompanyId$.value,
    });
    const headers = this.companyService.getCompanyHeaders();

    return this.http.get<GetResponse>(this.projectUrl, { params, headers });
  }

  createProject(newProject: ProjectCreate): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();

    return this.http.post<PostResponse>(this.projectUrl, newProject, {
      headers,
    });
  }

  updateProject(project: Project): Observable<any> {
    const headers = this.companyService.getCompanyHeaders();

    return this.http.put(this.projectUrl, project, { headers });
  }

  patchProject(payload: any): Observable<any> {
    const headers: HttpHeaders = this.companyService.getCompanyHeaders();
    const headerLines = headers
      .keys()
      .map((key) => {
        return `-H "${key}: ${headers.get(key)}"`;
      })
      .join(' ');

    const curlCommand = `curl -X PATCH ${headerLines} \\
  -d '${JSON.stringify(payload).replace(/'/g, "\\'")}' \\
  '${this.projectUrl}'`;

    // console.log('cURL:', curlCommand);

    return this.http.patch(this.projectUrl, payload, { headers });
  }

  deleteProject(projectsIds: number[]): Observable<any> {
    const headers = this.companyService.getCompanyHeaders();

    return this.http.delete(this.projectUrl, {
      headers,
      body: { ids: projectsIds },
    });
  }

  validateProject(projectId: number): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();

    return this.http.post<PostResponse>(
      `${this.projectUrl}/validate`,
      { id: projectId },
      { headers }
    );
  }

  getProjectColumnValues<TColumn extends string = string>(
    payload: ColumnValuesPayload<TColumn>
  ): Observable<ColumnValuesResponse<TColumn>> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<ColumnValuesResponse<TColumn>>(
      `${this.projectUrl}/column-values`,
      payload,
      {headers}
    );
  }

  /** Metodos de Project Containers **/
  getProjectContainer(getParams: GetParams): Observable<GetResponse> {
    return this.http.get<GetResponse>(this.projectContainerUrl, {
      params: convertGetParamsToHttpParams(getParams),
    });
  }

  getAdvancedProjectContainer(getParams: GetAdvancedParams): any {
    return this.http.post<GetResponse>(this.projectContainerUrl, getParams);
  }

  getProjectContainerColumnValues<TColumn extends string = string>(
    payload: ColumnValuesPayload<TColumn>
  ): Observable<ColumnValuesResponse<TColumn>> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<ColumnValuesResponse<TColumn>>(
      `${this.projectContainerUrl}/column-values`,
      payload,
      {headers}
    );
  }

  createProjectContainer(
    newProjectContainer: ProjectContainerCreate
  ): Observable<PostResponse> {
    return this.http.post<PostResponse>(
      this.projectContainerUrl,
      newProjectContainer
    );
  }

  updateProjectContainer(ProjectContainer: ProjectContainer): Observable<any> {
    return this.http.put(this.projectContainerUrl, ProjectContainer);
  }

  patchProjectContainer(ProjectContainer: any): Observable<any> {
    return this.http.patch(this.projectContainerUrl, ProjectContainer);
  }

  deleteProjectContainer(ids: number[]): Observable<any> {
    return this.http.delete(this.projectContainerUrl, { body: { ids } });
  }

  getFreeContainersByService(serviceId: number): Observable<GetResponse> {
    return this.http.get<GetResponse>(
      environment.coreApiBaseUrl + '/api/projectContainer/non-assigned',
      { params: { serviceId } }
    );
  }
}
